import {
  APIComponentAttributes,
  APIScopeData,
  ArdoqId,
  TypeId,
} from '@ardoq/api-types';
import { logError } from '@ardoq/logging';
import { isNonEmptyString } from 'integrations/common/modals/transformModal/transformSection/utils';
import { getAllTypeIds } from './validateUtils';

const buildModelIdsToTypeIds = (
  components: APIComponentAttributes[]
): Map<ArdoqId, Set<TypeId>> => {
  const modelIdsToTypeIds = new Map<ArdoqId, Set<TypeId>>();

  components
    .filter(
      ({ model, typeId }) => isNonEmptyString(model) && isNonEmptyString(typeId)
    )
    .forEach(({ model, typeId }) => {
      let types = modelIdsToTypeIds.get(model);
      if (!types) {
        types = new Set();
        modelIdsToTypeIds.set(model, types);
      }
      types.add(typeId);
    });

  return modelIdsToTypeIds;
};

const validateModelsAgainstScopeData = (
  modelIdsToTypeIds: Map<ArdoqId, Set<TypeId>>,
  scopeData: APIScopeData
): ValidationError[] => {
  const errors: ValidationError[] = [];

  modelIdsToTypeIds.forEach((expectedTypeIds, modelId) => {
    const model = scopeData.models.find(({ _id }) => _id === modelId) ?? null;
    if (!model) {
      errors.push({
        message: '[Inconsistent data] Model not found in scopeData',
        metadata: { modelId, context: 'scopeData' },
      });
      return;
    }
    const allTypeIdsFromScopeData = getAllTypeIds(model.root);
    for (const typeId of expectedTypeIds) {
      if (!allTypeIdsFromScopeData.has(typeId)) {
        errors.push({
          message: `[Inconsistent data] TypeId ${typeId} not found in scopeData model ${modelId}`,
          metadata: { modelId, typeId, context: 'scopeData' },
        });
      }
    }
  });

  return errors;
};

type ValidationError = {
  message: string;
  metadata: Record<string, unknown>;
};

const validateBasicComponentData = (
  components: APIComponentAttributes[]
): ValidationError[] => {
  const errors: ValidationError[] = [];
  components.forEach(({ _id, model, typeId }) => {
    if (!isNonEmptyString(model)) {
      errors.push({
        message: '[Inconsistent data] Missing model in component',
        metadata: { componentId: _id },
      });
    }
    if (!isNonEmptyString(typeId)) {
      errors.push({
        message: '[Inconsistent data] Missing typeId in component',
        metadata: { componentId: _id },
      });
    }
  });
  return errors;
};

const logValidationErrors = (errors: ValidationError[]): void => {
  errors.forEach(error => {
    logError(Error(error.message), null, error.metadata);
  });
};

type LogErrorIfInconsistentDataBaseParams = {
  components: APIComponentAttributes[];
  isLogErrors: boolean;
};

export type LogErrorIfInconsistentDataForScopeDataParams =
  LogErrorIfInconsistentDataBaseParams & {
    scopeData: APIScopeData;
  };

export const logErrorIfInconsistentDataForScopeData = ({
  components,
  scopeData,
  isLogErrors,
}: LogErrorIfInconsistentDataForScopeDataParams) => {
  const modelIdsToTypeIds = buildModelIdsToTypeIds(components);
  const basicErrors = validateBasicComponentData(components);
  const modelErrors = validateModelsAgainstScopeData(
    modelIdsToTypeIds,
    scopeData
  );

  if (isLogErrors) {
    logValidationErrors([...basicErrors, ...modelErrors]);
  }

  return basicErrors.length > 0 || modelErrors.length > 0;
};
